import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { LogOut, ShieldCheck, UserCircle2, Menu, X, Map as MapIcon, ChevronDown, Briefcase, LayoutDashboard, MapPin } from "lucide-react";
import CategoryIcon from "./CategoryIcon";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CATEGORIES, CATEGORY_ORDER } from "../lib/categories";
import { CHAD_REGIONS } from "../lib/chad";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/355f24cbb44947998348bd262690697f_image.png";

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? "text-brand-terracotta" : "text-brand-inkSoft hover:text-brand-ink"
    }`;

  return (
    <header
      className="sticky top-0 z-30 w-full backdrop-blur-xl bg-white/85 border-b border-brand-line"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Kanama"
            className="h-9 w-9 object-contain"
          />
          <div className="flex flex-col leading-tight">
            <span className="font-heading font-bold text-lg text-brand-ink tracking-tight">
              {t("brand.name")}
            </span>
            <span className="text-[9px] uppercase tracking-[0.15em] text-brand-gold font-semibold whitespace-nowrap max-w-[260px] truncate">
              {t("brand.tagline.short")}
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7" data-testid="primary-nav">
          <NavLink to="/" className={navLinkCls} end data-testid="nav-home">
            {t("nav.home")}
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger className="text-sm font-medium text-brand-inkSoft hover:text-brand-ink inline-flex items-center gap-1" data-testid="nav-categories">
              {t("nav.categories")} <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-white">
              {CATEGORY_ORDER.map((k) => {
                const c = CATEGORIES[k];
                return (
                  <DropdownMenuItem key={k} asChild>
                    <Link to={`/${c.slug}`} className="flex items-center gap-3" data-testid={`nav-cat-${k}`}>
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: c.color }}
                      >
                        <CategoryIcon name={c.icon} className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm">{t(`cat.${k}`)}</span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/carte" className={navLinkCls} data-testid="nav-carte">
            <MapIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
            {t("nav.map")}
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger className="text-sm font-medium text-brand-inkSoft hover:text-brand-ink inline-flex items-center gap-1" data-testid="nav-regions">
              {t("nav.regions")} <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[640px] max-w-[92vw] bg-white p-3 grid grid-cols-2 sm:grid-cols-3 gap-1" align="start">
              {CHAD_REGIONS.map((r) => (
                <DropdownMenuItem key={r.region} asChild>
                  <Link
                    to={`/carte?city=${encodeURIComponent(r.capital)}`}
                    className="flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-brand-sandlight"
                    data-testid={`nav-region-${r.region.replace(/[^a-zA-Z]/g, "-")}`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-brand-gold mt-0.5 shrink-0" />
                    <div className="flex flex-col leading-tight">
                      <span className="text-sm font-medium text-brand-ink">{r.region}</span>
                      <span className="text-[10px] text-brand-inkMuted uppercase tracking-wider">{r.capital}</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/ride" className={navLinkCls} data-testid="nav-ride">
            🚕 Ride
          </NavLink>

          <NavLink to="/courses" className={navLinkCls} data-testid="nav-courses">
            🛒 Courses
          </NavLink>

          <NavLink to="/restaurants/order" className={navLinkCls} data-testid="nav-restaurants-order">
            🍽️ Repas
          </NavLink>

          <NavLink to="/tarifs" className={navLinkCls} data-testid="nav-pricing">
            {t("nav.pricing")}
          </NavLink>

          {user && (user.role === "owner" || user.role === "admin") && (
            <NavLink to="/annonceur" className={navLinkCls} data-testid="nav-owner-dashboard">
              <LayoutDashboard className="w-4 h-4 inline mr-1 -mt-0.5" />
              {t("nav.ownerDashboard")}
            </NavLink>
          )}

          {user && user.role === "admin" && (
            <NavLink to="/admin" className={navLinkCls} data-testid="nav-admin">
              {t("nav.admin")}
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {(!user || user === false || user.role === "user") && (
            <Link to="/devenir-annonceur" className="hidden md:inline-block" data-testid="nav-become-owner">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-brand-gold text-brand-goldDark hover:bg-brand-gold/10 font-semibold"
              >
                <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                {t("nav.becomeOwner")}
              </Button>
            </Link>
          )}
          <LanguageSwitcher />
          {user && user !== false ? (
            <>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-sandlight border border-brand-line">
                {user.role === "admin" ? (
                  <ShieldCheck className="w-4 h-4 text-brand-forest" />
                ) : (
                  <UserCircle2 className="w-4 h-4 text-brand-inkSoft" />
                )}
                <span className="text-xs font-medium text-brand-ink" data-testid="current-user-name">
                  {user.name}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-brand-inkSoft hover:text-brand-ink rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-link">
                <Button variant="ghost" size="sm" className="rounded-full text-brand-inkSoft hover:text-brand-ink">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register" data-testid="register-link" className="hidden sm:inline-block">
                <Button
                  size="sm"
                  className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white shadow-sm"
                >
                  {t("nav.register")}
                </Button>
              </Link>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen((s) => !s)}
            className="md:hidden text-brand-inkSoft rounded-full"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Refined mobile drawer — SECONDARY menu (primary actions live on BottomNav) */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-drawer"
        >
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/45 animate-in fade-in duration-200"
            data-testid="mobile-drawer-backdrop"
          />
          <div
            className="absolute top-0 right-0 h-full w-[82vw] max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
            style={{ paddingTop: "env(safe-area-inset-top, 0)", paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
              <div className="flex items-center gap-2.5">
                <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain" />
                <div className="flex flex-col leading-tight">
                  <span className="font-heading font-bold text-base text-brand-ink">{t("brand.name")}</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] text-brand-gold font-semibold">
                    {t("brand.tagline.short")}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="-mr-2 p-2 rounded-full text-brand-inkSoft active:bg-brand-sandlight min-h-[44px] min-w-[44px] flex items-center justify-center"
                data-testid="mobile-drawer-close"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Account quick-info */}
              {user && user !== false && (
                <div className="flex items-center gap-3 mb-5 p-3 rounded-2xl bg-brand-sandlight border border-brand-line">
                  {user.role === "admin" ? (
                    <ShieldCheck className="w-5 h-5 text-brand-forest shrink-0" />
                  ) : (
                    <UserCircle2 className="w-5 h-5 text-brand-inkSoft shrink-0" />
                  )}
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-brand-ink">{user.name}</div>
                    <div className="text-[11px] text-brand-inkMuted uppercase tracking-wider">{user.email}</div>
                  </div>
                </div>
              )}

              {/* Categories */}
              <div className="pb-1 text-[10px] uppercase tracking-wider text-brand-inkMuted font-bold">
                {t("nav.categories")}
              </div>
              <div className="space-y-0.5 mb-5">
                {CATEGORY_ORDER.map((k) => {
                  const c = CATEGORIES[k];
                  return (
                    <Link
                      key={k}
                      to={`/${c.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl active:bg-brand-sandlight min-h-[44px] touch-manipulation"
                      data-testid={`mobile-nav-cat-${k}`}
                    >
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: c.color }}
                      >
                        <CategoryIcon name={c.icon} className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm text-brand-ink">{t(`cat.${k}`)}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Quick links */}
              <div className="pb-1 text-[10px] uppercase tracking-wider text-brand-inkMuted font-bold">
                {t("nav.more")}
              </div>
              <div className="space-y-0.5">
                {(!user || user === false || user.role === "user") && (
                  <Link
                    to="/devenir-annonceur"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl text-brand-goldDark font-semibold active:bg-brand-sandlight min-h-[44px]"
                    data-testid="mobile-nav-become-owner"
                  >
                    <Briefcase className="w-4 h-4" />
                    {t("nav.becomeOwner")}
                  </Link>
                )}
                {user && (user.role === "owner" || user.role === "admin") && (
                  <Link
                    to="/annonceur"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl text-brand-terracotta font-semibold active:bg-brand-sandlight min-h-[44px]"
                    data-testid="mobile-nav-owner-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t("nav.ownerDashboard")}
                  </Link>
                )}
                {user && user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl text-brand-terracotta font-semibold active:bg-brand-sandlight min-h-[44px]"
                    data-testid="mobile-nav-admin"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {t("nav.admin")}
                  </Link>
                )}
                {user && user !== false && (
                  <button
                    type="button"
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl text-brand-inkSoft font-medium active:bg-brand-sandlight min-h-[44px]"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    {t("nav.logout")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
